import { generateClient } from 'aws-amplify/api';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { fetchNotifications } from '../menu';
import type { driverIndividualNotification } from '@/API';
import { MobiTitle } from '@/components/shared/MobiTitle';
import * as mutations from '@/graphql/mutations';
import * as paths from '@/routes/paths';
import { settingState } from '@/state/settingState';

type ParamType = { driverId: string; createdAt: string };
type DetailSection = { title: string; lines: string[] };
type NotificationPageProps = {
  mockNotifications?: driverIndividualNotification[];
};

const ChevronLeftIcon: React.FC = () => (
  <svg className="nav-icon nav-icon-back" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14.5 5L7.5 12L14.5 19" />
  </svg>
);

type NotificationLineProps = {
  line: string;
  lineIndex: number;
  sectionTitle: string;
};

const NotificationLine: React.FC<NotificationLineProps> = ({ line, lineIndex, sectionTitle }) => {
  const isMetaLabel = line === '予定時刻' || line === '停車地点';
  const isPassengerValue = sectionTitle.includes('利用人数') && lineIndex === 0;
  const className = isMetaLabel
    ? 'notification-detail-meta-label'
    : isPassengerValue
      ? 'notification-detail-value is-emphasis'
      : 'notification-detail-value';

  return <p className={className}>{line}</p>;
};

const ChevronRightIcon: React.FC<{ expanded?: boolean }> = ({ expanded = false }) => (
  <svg className={`nav-icon nav-icon-chevron ${expanded ? 'is-expanded' : ''}`} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9.5 5L16.5 12L9.5 19" />
  </svg>
);

export const NotificationPage: React.FC<NotificationPageProps> = ({ mockNotifications }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [setting] = useRecoilState(settingState);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [informationShow, setInformationShow] = useState<boolean>(false);

  const [driverIndividualNotificationsInfo, setDriverIndividualNotificationsInfo] = useState<
    driverIndividualNotification[]
  >([]);

  // ドライバID（cognitoのdriverId）
  const globalDriverId = setting?.driverId || '';

  // ドライバ個別通知一覧取得APIを実行する
  const getNotificationsInfo = useCallback(async (driverId: string) => {
    const [responseDriverIndividualNotifications] = await fetchNotifications(driverId, 3);
    if (!responseDriverIndividualNotifications || responseDriverIndividualNotifications.length === 0) {
      setDriverIndividualNotificationsInfo([]);
      setInformationShow(true);

      return;
    }

    setDriverIndividualNotificationsInfo(responseDriverIndividualNotifications);
    setInformationShow(true);
  }, []);

  useEffect(() => {
    if (mockNotifications) {
      setDriverIndividualNotificationsInfo(mockNotifications);
      setInformationShow(true);

      return;
    }

    // 画面初期処理
    setInformationShow(false);
    void getNotificationsInfo(globalDriverId);
  }, [globalDriverId, mockNotifications, getNotificationsInfo]);

  // ドライバ個別通知更新APIからドライバ個別通知を更新する
  const UpdDriverIndividualNotificationsInfo = async ({ driverId, createdAt }: ParamType) => {
    const client = generateClient();
    const variables = {
      input: {
        driverId,
        createdAt,
        isRead: true,
      },
      condition: { driverId: { eq: driverId }, createdAt: { eq: createdAt } },
    };

    const updRes = await client.graphql({ query: mutations.updateDriverIndividualNotification, variables });

    if (!updRes?.errors) {
      setDriverIndividualNotificationsInfo((prevItems) =>
        prevItems.map((item) =>
          item.driverId === driverId && item.createdAt === createdAt ? { ...item, isRead: true } : item,
        ),
      );
    }
  };

  const handleDriverClick = (item: driverIndividualNotification) => {
    const key = `${item.driverId}-${item.createdAt}`;

    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };

      return next;
    });

    if (!item.isRead) {
      if (mockNotifications) {
        setDriverIndividualNotificationsInfo((prevItems) =>
          prevItems.map((prevItem) =>
            prevItem.driverId === item.driverId && prevItem.createdAt === item.createdAt
              ? { ...prevItem, isRead: true }
              : prevItem,
          ),
        );

        return;
      }

      UpdDriverIndividualNotificationsInfo({ driverId: item.driverId, createdAt: item.createdAt }).catch(() => {});
    }
  };

  const splitTextToArray = (text: string) => {
    if (!text) {
      return [text];
    }
    const tempText = text.replace(/\r/, '');

    if (tempText.includes('\\n')) {
      return tempText.split(/\\n/);
    }
    if (tempText.includes('\n')) {
      return tempText.split(/\n/);
    }

    return [text];
  };

  const parseDetailContent = (text: string): { lead: string; sections: DetailSection[] } => {
    const normalizedLines = splitTextToArray(text)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (normalizedLines.length === 0) {
      return { lead: '', sections: [] };
    }

    const [lead, ...rest] = normalizedLines;
    const sections: DetailSection[] = [];
    let currentSection: DetailSection | null = null;

    rest.forEach((line) => {
      if (line.startsWith('■')) {
        if (currentSection) {
          sections.push(currentSection);
        }
        currentSection = { title: line, lines: [] };

        return;
      }

      if (!currentSection) {
        currentSection = { title: '', lines: [] };
      }
      currentSection.lines.push(line);
    });

    if (currentSection) {
      sections.push(currentSection);
    }

    return { lead, sections };
  };

  const getItemDate = (createdAt: string) => {
    const parsedDate = new Date(createdAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return '';
    }

    const year = parsedDate.getFullYear();
    const month = parsedDate.getMonth() + 1;
    const date = parsedDate.getDate();

    return `${year}年${month}月${date}日`;
  };

  return (
    <div className="notification-surface">
      <MobiTitle>{t('notification.header')}</MobiTitle>

      <div className="notification-top-bar">
        <button
          className="notification-icon-button"
          type="button"
          onClick={() => {
            navigate(paths.menu);
          }}
          aria-label={t('notification.back')}
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="notification-header-text">{t('notification.header')}</h1>
      </div>

      <div className="notification-content-shell">
        {informationShow &&
          (driverIndividualNotificationsInfo.length === 0 ? (
            <div className="notification-empty-state">{t('notification.noData')}</div>
          ) : (
            <div className="notification-card-list">
              {driverIndividualNotificationsInfo.map((item) => {
                const key = `${item.driverId}-${item.createdAt}`;
                const isExpanded = Boolean(expanded[key]);

                return (
                  <button
                    key={key}
                    className={`notification-card-button ${isExpanded ? 'is-expanded' : ''}`}
                    type="button"
                    onClick={() => handleDriverClick(item)}
                  >
                    {isExpanded ? (
                      <div className="notification-detail-block">
                        {(() => {
                          const detailContent = parseDetailContent(item.body);

                          return (
                            <>
                              {detailContent.lead && <p className="notification-detail-lead">{detailContent.lead}</p>}
                              {detailContent.sections.map((section, sectionIndex) => (
                                <React.Fragment key={`${key}-${section.title}-${section.lines.join('|')}`}>
                                  <div className="notification-detail-section">
                                    {section.title && (
                                      <p className="notification-detail-section-title">{section.title}</p>
                                    )}
                                    <div className="notification-detail-section-body">
                                      {section.lines.map((line) => (
                                        <NotificationLine
                                          // keyは親で管理
                                          key={`${key}-${section.title}-${line}`}
                                          line={line}
                                          lineIndex={section.lines.indexOf(line)}
                                          sectionTitle={section.title}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                  {sectionIndex < detailContent.sections.length - 1 && (
                                    <div className="notification-detail-divider" />
                                  )}
                                </React.Fragment>
                              ))}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="notification-summary-block">
                        <p className="notification-summary-title">{item.subject}</p>
                        <p className="notification-summary-date">{getItemDate(item.createdAt)}</p>
                      </div>
                    )}
                    <span className={`notification-chevron-holder ${isExpanded ? 'is-bottom' : ''}`}>
                      <ChevronRightIcon expanded={isExpanded} />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
      </div>
    </div>
  );
};
